// MCHD Room Scheduler - Holiday Management Hook
// React Query hooks for holiday management operations

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../useAuth';
import {
  getHolidays,
  getHolidaysByBuilding,
  getHolidayCalendar,
  getHoliday,
  createHoliday,
  updateHoliday,
  deleteHoliday,
  getBuildings,
  type GetHolidaysParams,
  type CreateHolidayData,
  type UpdateHolidayData,
} from '../../api/endpoints/holidays.js';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const holidayQueryKeys = {
  all: ['holidays'] as const,
  lists: () => [...holidayQueryKeys.all, 'list'] as const,
  list: (params: GetHolidaysParams) => [...holidayQueryKeys.lists(), params] as const,
  details: () => [...holidayQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...holidayQueryKeys.details(), id] as const,
  byBuilding: (buildingId: string, year?: number, month?: number) => 
    [...holidayQueryKeys.all, 'building', buildingId, { year, month }] as const,
  calendar: (buildingId: string, year: number, month: number) => 
    [...holidayQueryKeys.all, 'calendar', buildingId, year, month] as const,
  buildings: () => [...holidayQueryKeys.all, 'buildings'] as const,
};

// =============================================================================
// HOLIDAY LIST HOOK
// =============================================================================

/**
 * Hook to get holidays list with pagination and filtering
 * @param params - Query parameters
 * @returns Holiday list query result
 */
export function useHolidays(params: GetHolidaysParams = {}) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: holidayQueryKeys.list(params),
    queryFn: () => getHolidays(params, token!),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

// =============================================================================
// HOLIDAY BY BUILDING HOOK
// =============================================================================

/**
 * Hook to get holidays by building
 * @param buildingId - Building ID
 * @param year - Optional year filter
 * @param month - Optional month filter
 * @returns Holidays by building query result
 */
export function useHolidaysByBuilding(
  buildingId: string,
  year?: number,
  month?: number
) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: holidayQueryKeys.byBuilding(buildingId, year, month),
    queryFn: () => getHolidaysByBuilding(buildingId, token!, year, month),
    enabled: !!token && !!buildingId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

// =============================================================================
// HOLIDAY CALENDAR HOOK
// =============================================================================

/**
 * Hook to get holiday calendar for a building
 * @param buildingId - Building ID
 * @param year - Year
 * @param month - Month (1-12)
 * @returns Holiday calendar query result
 */
export function useHolidayCalendar(
  buildingId: string,
  year: number,
  month: number
) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: holidayQueryKeys.calendar(buildingId, year, month),
    queryFn: () => getHolidayCalendar(buildingId, year, month, token!),
    enabled: !!token && !!buildingId && !!year && !!month,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

// =============================================================================
// HOLIDAY DETAIL HOOK
// =============================================================================

/**
 * Hook to get holiday by ID
 * @param id - Holiday ID
 * @returns Holiday detail query result
 */
export function useHoliday(id: string) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: holidayQueryKeys.detail(id),
    queryFn: () => getHoliday(id, token!),
    enabled: !!token && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

// =============================================================================
// BUILDINGS HOOK
// =============================================================================

/**
 * Hook to get all buildings for holiday management
 * @returns Buildings query result
 */
export function useBuildings() {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: holidayQueryKeys.buildings(),
    queryFn: () => getBuildings(token!),
    enabled: !!token,
    staleTime: 10 * 60 * 1000, // 10 minutes (buildings don't change often)
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

// =============================================================================
// CREATE HOLIDAY MUTATION
// =============================================================================

/**
 * Hook to create a new holiday
 * @returns Create holiday mutation
 */
export function useCreateHoliday() {
  const { getStoredToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateHolidayData) => {
      const token = getStoredToken();
      return createHoliday(data, token!);
    },
    onSuccess: (_data, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: holidayQueryKeys.lists() });
      queryClient.invalidateQueries({ 
        queryKey: holidayQueryKeys.byBuilding(variables.buildingId) 
      });
      queryClient.invalidateQueries({ 
        queryKey: holidayQueryKeys.calendar(variables.buildingId, 
          new Date(variables.dateDay).getFullYear(),
          new Date(variables.dateDay).getMonth() + 1
        )
      });
    },
    onError: (error) => {
      console.error('Error creating holiday:', error);
    },
  });
}

// =============================================================================
// UPDATE HOLIDAY MUTATION
// =============================================================================

/**
 * Hook to update a holiday
 * @returns Update holiday mutation
 */
export function useUpdateHoliday() {
  const { getStoredToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateHolidayData }) => {
      const token = getStoredToken();
      return updateHoliday(id, data, token!);
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: holidayQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: holidayQueryKeys.detail(variables.id) });
      
      // If date changed, invalidate calendar queries
      if (variables.data.dateDay) {
        const newDate = new Date(variables.data.dateDay);
        queryClient.invalidateQueries({ 
          queryKey: holidayQueryKeys.calendar(
            data.data?.holiday.buildingId || '',
            newDate.getFullYear(),
            newDate.getMonth() + 1
          )
        });
      }
    },
    onError: (error) => {
      console.error('Error updating holiday:', error);
    },
  });
}

// =============================================================================
// DELETE HOLIDAY MUTATION
// =============================================================================

/**
 * Hook to delete a holiday
 * @returns Delete holiday mutation
 */
export function useDeleteHoliday() {
  const { getStoredToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      const token = getStoredToken();
      return deleteHoliday(id, token!);
    },
    onSuccess: (_data, id) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: holidayQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: holidayQueryKeys.detail(id) });
      
      // Remove the specific holiday from cache
      queryClient.removeQueries({ queryKey: holidayQueryKeys.detail(id) });
    },
    onError: (error) => {
      console.error('Error deleting holiday:', error);
    },
  });
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook to get holiday statistics
 * @param buildingId - Optional building ID filter
 * @returns Holiday statistics
 */
export function useHolidayStats(buildingId?: string) {
  const { data: holidaysData, isLoading, error } = useHolidays({
    ...(buildingId && { buildingId }),
    limit: 100, // Get all holidays for stats
  });

  const stats = holidaysData?.success ? {
    total: holidaysData.data?.pagination.total || 0,
    thisYear: holidaysData.data?.holidays.filter(h => 
      new Date(h.dateDay).getFullYear() === new Date().getFullYear()
    ).length || 0,
    thisMonth: holidaysData.data?.holidays.filter(h => {
      const holidayDate = new Date(h.dateDay);
      const now = new Date();
      return holidayDate.getFullYear() === now.getFullYear() && 
             holidayDate.getMonth() === now.getMonth();
    }).length || 0,
    upcoming: holidaysData.data?.holidays.filter(h => 
      new Date(h.dateDay) > new Date()
    ).length || 0,
  } : null;

  return {
    stats,
    isLoading,
    error,
  };
}

/**
 * Hook to check if a specific date is a holiday
 * @param buildingId - Building ID
 * @param date - Date to check
 * @returns Boolean indicating if date is a holiday
 */
export function useIsHoliday(buildingId: string, date: Date) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  
  const { data: calendarData } = useHolidayCalendar(buildingId, year, month);
  
  if (!calendarData?.success || !calendarData.data) {
    return false;
  }

  const dateStr = date.toISOString().split('T')[0];
  return calendarData.data.holidays.some(holiday => 
    holiday.dateDay.split('T')[0] === dateStr
  );
}
